/**
 * Authentication Store
 *
 * Manages user authentication state, tokens, and user information.
 * Handles login/logout API calls directly in the store.
 *
 * Token refresh:
 *   • Proactive: schedules a refresh ~60 s before accessToken expiry
 *   • Reactive:  401 interceptor in api.service.ts calls refreshAccessToken()
 *
 * Locale integration:
 *   • After login:  loads tenant-scoped translation bundle via locale store
 *   • After logout: resets locale state so next login re-fetches for new tenant
 */
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import i18next from 'i18next';
import type { BaseUser, TotpInitiateResponse, MfaStatus, ForceLogoutReason } from '@tms-phoenix/tms-types';
import { hasIdleTimeoutElapsed, clearLastActivityTimestamp } from '@tms-phoenix/ui-vue';
import { getApiClient } from '@/core/services/api.service';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import { logAuditEvent } from '@/core/services/audit.service';
import { API_ROUTES, setAuthContext, clearAuthContext } from '@/core/config';
import { router } from '@/router/instance';
import type { AuthLoginResponse, AuthLoginChallenge, AuthResetPasswordResponse } from '@/modules/auth/types';
import type { UserDetailsResponse } from '@/modules/auth/services/auth.service';
import { fetchUserDetails } from '@/modules/auth/services/auth.service';
import { useLocaleStore } from '@/stores/locale.store';
// Dependency-free localStorage helpers (no store imports), so this is safe to
// import statically — unlike settings.store.ts, which imports this store.
import {
  readCompletedSteps,
  readOnboardingStarted,
  writeCompletedSteps,
  writeOnboardingStarted,
  clearOnboardingProgress,
  writeIsSettingsConfigured,
  clearIsSettingsConfigured,
} from '@/modules/settings/onboardingProgress';

// ── Proactive refresh timer ─────────────────────────────────────────────────
 let _refreshTimerId: ReturnType<typeof setTimeout> | null = null;

/** In-flight refresh promise mutex — prevents concurrent refresh calls. */
let _refreshPromise: Promise<boolean> | null = null;

/**
 * Decode the base64url-encoded payload segment of a JWT **without verifying
 * the signature or any standard claims (iss, aud, exp)**.
 *
 * SECURITY: The returned object is UNVERIFIED and MUST NOT be used for
 * any authorization or access-control decision. The sole legitimate use is
 * scheduling a proactive token-refresh timer before `exp` is reached — see
 * `decodeJwtExp()`. `is_settings_configured` is no longer read from here; it
 * comes from the GET /users/:username API response instead (see
 * `_finishLogin()`/`setAuth()`), since the Pre-Token-Generation Lambda no
 * longer injects a `custom:session_ctx` claim to decode.
 *
 * All server-enforced authorization (roles, permissions, tenant access)
 * derives from the API response body, never from this function.
 */
function parseJwtClaimsUnsafe(token: string): Record<string, unknown> | null {
  try {
    let payload = token.split('.')[1];
    if (!payload) return null;
    // Restore base64url → base64 and pad to a multiple of 4
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4 !== 0) payload += '=';
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/** Extract `exp` (Unix seconds) from a JWT for refresh-scheduling purposes only. No signature verification. */
function decodeJwtExp(token: string): number | null {
  const json = parseJwtClaimsUnsafe(token);
  return json && typeof json.exp === 'number' ? json.exp : null;
}

const FALLBACK_ROLES = new Set(['NO_ROLE', 'DEFAULT', 'DEFAULT_ROLE']);

function getEffectiveRoles(parsedRoles: string[]): string[] {
  const normalized = parsedRoles
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().toUpperCase())
    .filter((role) => role.length > 0);

  // Treat fallback roles as "role-less" markers once a concrete role exists.
  const concreteRoles = normalized.filter((role) => !FALLBACK_ROLES.has(role));
  return concreteRoles.length > 0 ? concreteRoles : normalized;
}

interface LoginCredentials {
  email: string;
  password: string;
}

type ForgotPasswordPayload = {
  email: string;
};

interface AuthState {
  user: BaseUser | null;
  /** Full user details from onboarding service (persisted to localStorage). */
  userDetails: UserDetailsResponse | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** Username from login response (used to fetch user details). */
  username: string | null;
  /** Staff ID from user details response (`sub`). */
  staffId: string | null;
  /** Hospital ID from login/user-details response. */
  hospitalId: string | null;
  /** Tenant ID from login/user-details response. */
  tenantId: string | null;
  /** Raw roles string from login response (JSON-encoded). */
  roles: string | null;
  /** Parsed roles array (derived from JSON roles string). */
  parsedRoles: string[];
  /** User's default language code from login/reset response (e.g. 'en', 'fr', 'de'). */
  defaultLanguage: string | null;
  /** Whether the tenant has completed first-time settings configuration (from GET /users/:username). */
  isSettingsConfigured: boolean | null;
  /** Pending auth challenge data (e.g. NEW_PASSWORD_REQUIRED, SOFTWARE_TOKEN_MFA, EMAIL_OTP). */
  challengeData: AuthLoginChallenge['challenge'] | null;
  /** True when login succeeded but MFA has not been configured yet. */
  mfaSetupRequired: boolean;
  /** Which MFA challenge type is pending (set after login returns MFA challenge). */
  mfaChallengeType: 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | null;
  /** QR code data from POST /mfa/setup/totp/initiate (held while user scans). */
  totpQrData: TotpInitiateResponse | null;
  /** Temporary access token returned with SELECT_MFA_TYPE challenge, used to authenticate MFA setup calls. */
  mfaSetupAccessToken: string | null;
  /** Email the user typed at login — fallback username when challengeParameters lacks USER_ID_FOR_SRP. */
  pendingEmail: string | null;
  isLoading: boolean;
  error: string | null;
  /** Error code returned by the API on login failure (e.g. 'USER_DISABLED'). */
  loginErrorCode: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    userDetails: null,
    accessToken: null,
    refreshToken: null,
    username: null,
    staffId: null,
    hospitalId: null,
    tenantId: null,
    roles: null,
    parsedRoles: [],
    defaultLanguage: null,
    isSettingsConfigured: null,
    challengeData: null,
    mfaSetupRequired: false,
    mfaChallengeType: null,
    totpQrData: null,
    mfaSetupAccessToken: null,
    pendingEmail: null,
    isLoading: false,
    error: null,
    loginErrorCode: null,
  }),

  getters: {
    // PHX-1180: no longer gated on accessToken — the real session lives in an
    // httpOnly cookie the SPA can't read. accessToken is now only an in-memory,
    // never-persisted convenience value (proactive-refresh scheduling, legacy
    // Authorization header). A missing accessToken after a page reload does NOT
    // mean the user is logged out; the API client/backend resolve auth via cookie.
    isAuthenticated: (state): boolean => !!state.user,
    userRole: (state): string | undefined => state.user?.role,
    userName: (state): string | undefined => state.user?.name || state.user?.email,
    userEmail: (state): string | undefined => state.user?.email,
  },

  actions: {
    /**
     * Post-login side-effects shared by ALL login methods (credentials, SSO, password reset).
     *
     * Centralising these here means adding a new post-login step automatically
     * applies to every flow — the SSO callback and credentials login stay in sync
     * without manual maintenance.
     *
     * Must be called AFTER auth state (tokens, user, isSettingsConfigured) is already set.
     * All effects are non-fatal / fire-and-forget.
     */
    async _runPostLoginSideEffects(): Promise<void> {
      // 1. Persist isSettingsConfigured so the settings guard can read it
      //    on page refresh without an extra API round-trip.
      if (this.isSettingsConfigured !== null) {
        writeIsSettingsConfigured(this.isSettingsConfigured);
      } else {
        clearIsSettingsConfigured();
      }

      // 1b. If first-time setup isn't done, reconcile step progress with the
      //     backend so a login on a new browser/device resumes correctly instead
      //     of showing "not started."  Not awaited, like every other effect here.
      //
      //     Reconciliation is a *union*, not an overwrite, in both directions:
      //       - remote -> local, so another device's progress shows up here;
      //       - local -> remote, because each step's PATCH during onboarding is
      //         fire-and-forget, so a dropped write leaves a step recorded
      //         locally but missing server-side.  Re-PATCHing the difference
      //         makes that divergence self-correcting on the next login instead
      //         of permanent.  The backend ADDs to a String Set, so re-sending a
      //         step the server already knows about is a no-op.
      //
      //     The store is re-hydrated afterwards rather than left for the next
      //     page load: the router guard resolves onboarding state once per
      //     session and would otherwise win the race against this fetch and
      //     never re-read it.
      // Lazy imports: settings.api.ts calls getApiClient() eagerly at module
      // scope, and settings.store.ts imports this store — a static import of
      // either here would introduce eager load-order/circular-dependency
      // side effects (mirrors the lazy import guards.ts already uses for
      // settings.store.ts, for the same reason).
      // ── BEGIN LOCAL_ONBOARDING_OVERRIDE ─────────────────────────────────────
      // TEMPORARY (local dev only). isSettingsConfigured above is the JWT claim
      // baked from the shared cloud DynamoDB tenant item, which can't be
      // trusted once the Postgres override is on — without this, reconciliation
      // skips itself on that stale value and a relogin never re-syncs
      // onboarding_started/completed_steps from Postgres, so the splash comes
      // back even though Postgres already has real progress.
      const { isLocalOnboardingOverrideActive } = await import('@/core/config');
      const onboardingOverrideActive = isLocalOnboardingOverrideActive();
      // ── END LOCAL_ONBOARDING_OVERRIDE ───────────────────────────────────────
      if ((this.isSettingsConfigured === false || onboardingOverrideActive) && this.hospitalId) {
        void (async () => {
          try {
            const { fetchOnboardingProgress, addOnboardingStepApi } = await import('@/modules/settings/services/settings.api');
            const { completed_steps, started } = await fetchOnboardingProgress();
            const { useSettingsStore } = await import('@/stores/settings.store');

            const localSteps = readCompletedSteps();
            const merged = Array.from(new Set([...completed_steps, ...localSteps]));
            writeCompletedSteps(merged);
            writeOnboardingStarted(started || readOnboardingStarted());
            useSettingsStore().hydrateOnboardingProgress();

            // Push back anything the server was missing (best-effort, idempotent).
            for (const key of localSteps.filter((k) => !completed_steps.includes(k))) {
              addOnboardingStepApi({ stepKey: key }).catch(() => { /* non-fatal */ });
            }
          } catch {
            /* non-fatal — localStorage progress (if any) still applies */
          }
        })();
      }

      // 2. Load tenant-scoped translations (fire-and-forget)
      try {
        const localeStore = useLocaleStore();
        localeStore.loadFullBundle().catch(() => { /* non-fatal */ });
      } catch {
        // Non-fatal: embedded fallback translations still work
      }

      // 3. Fetch plan + role entitlements (fire-and-forget)
      try {
        const { useEntitlementsStore } = await import('@/stores/entitlements.store');
        const entitlementsStore = useEntitlementsStore();
        entitlementsStore.fetchEntitlements().catch(() => { /* non-fatal */ });
      } catch {
        // Non-fatal: sidebar will show all items until entitlements load
      }

      // 4. Republish the tenant's MRN term from the authenticated hospital
      //    (fire-and-forget, SV-334). The login page already published a
      //    provisional term taken from the pre-auth tenant-config response, but
      //    that endpoint resolves the tenant with a non-unique scan on
      //    tenant_hostname, so on a shared domain it can name a different
      //    hospital than the one just signed into. This read is scoped to
      //    hospital_id and supersedes it — the same rule the settings read path
      //    already follows. Runs after setAuthContext(), so config.hospitalId is
      //    populated; ensureSupportEmail() no-ops if it somehow isn't.
      //
      //    Hooked here rather than in each login path because this is the one
      //    place both SSO (useOidc.handleCallback → setAuth) and credential
      //    login (_finishLogin) converge.
      try {
        const { useTenantSupportEmail } = await import('@/shared/composables/useTenantSupportEmail');
        useTenantSupportEmail().ensureSupportEmail().catch(() => { /* non-fatal */ });
      } catch {
        // Non-fatal: the provisional term from the login page stays in force
      }
    },

    /**
     * Re-runs post-login side effects after MFA setup verify, mirroring what
     * respondMfaChallenge does via _finishLogin. Resets entitlements first so the
     * generation counter discards any in-flight fetch from the login phase.
     */
    async resetEntitlementsForReload(): Promise<void> {
      try {
        const { useEntitlementsStore } = await import('@/stores/entitlements.store');
        const entitlementsStore = useEntitlementsStore();
        entitlementsStore.reset();
      } catch {
        // Non-fatal
      }
      void this._runPostLoginSideEffects();
    },

    /**
     * Shared post-authentication flow used by both login() and completeNewPasswordChallenge().
     *
     * Handles: role parsing, token persistence, user details fetch, BaseUser build,
     * auth context sync, proactive refresh scheduling, and locale bundle loading.
     *
     * @param params.accessToken  - JWT access token from login/reset response
     * @param params.refreshToken - Refresh token from login/reset response
     * @param params.loginUser    - User object from login/reset response
     * @param params.fallbackEmail - Email to use as fallback display name (e.g. credentials.email)
     * @returns true on success, false on failure (sets this.error)
     */
    async _finishLogin(params: {
      accessToken: string;
      refreshToken: string;
      loginUser: { username: string; tenant_id?: string; hospital_id: string; roles: string; default_language?: string };
      fallbackEmail: string;
    }): Promise<boolean> {
      const { accessToken, refreshToken, loginUser, fallbackEmail } = params;
      const { username, tenant_id: tenantId, hospital_id: hospitalId, roles: rolesRaw, default_language: defaultLang } = loginUser;

      // ── Parse roles safely (JSON string → string[]) ───────────────────
      let parsedRoles: string[] = [];
      try {
        const parsed = JSON.parse(rolesRaw);
        if (Array.isArray(parsed)) {
          parsedRoles = parsed.filter((r): r is string => typeof r === 'string');
        }
      } catch {
        parsedRoles = [];
      }

      // ── Persist login-derived data ────────────────────────────────────
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      this.username = username;
      this.tenantId = tenantId ?? null;
      this.hospitalId = hospitalId;
      this.roles = rolesRaw;
      this.parsedRoles = parsedRoles;
      this.defaultLanguage = defaultLang ?? null;

      // ── Fetch full user details (GET /users/:username) ────────────────
      let staffId = '';
      let userDetails: UserDetailsResponse | null = null;
      const maxRetries = 2;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          userDetails = await fetchUserDetails(username, tenantId ?? hospitalId);
          staffId = userDetails.sub ?? '';
          if (userDetails.hospital_id) {
            this.hospitalId = userDetails.hospital_id;
          }
          if (userDetails.tenant_id) {
            this.tenantId = userDetails.tenant_id;
          }
          break;
        } catch (detailsErr) {
          if (attempt === maxRetries) {
            const safeMsg =
              detailsErr instanceof Error ? detailsErr.message : String(detailsErr);
            const status =
              detailsErr != null &&
              typeof detailsErr === 'object' &&
              'response' in detailsErr &&
              (detailsErr as { response?: { status?: unknown } }).response?.status != null
                ? (detailsErr as { response: { status: unknown } }).response.status
                : undefined;
            console.error(
              'Failed to fetch user details after retries:',
              status != null ? `HTTP ${status} — ${safeMsg}` : safeMsg,
            );
            this.clearAuth();
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('idToken');
            this.error = i18next.t('auth.fetchUserDetailsFailed');
            return false;
          }
          await new Promise((r) => setTimeout(r, 1000));
        }
      }

      if (!staffId) {
        this.clearAuth();
        this.error = i18next.t('auth.staffIdMissing');
        return false;
      }

      this.staffId = staffId;
      this.userDetails = userDetails;

      // `is_settings_configured` comes from the GET /users/:username response
      // (user.service.ts's getUser()) — UI routing hint only, the API enforces
      // this independently server-side. No longer decoded from the access
      // token's custom:session_ctx claim, which the Pre-Token-Generation
      // Lambda no longer injects.
      this.isSettingsConfigured = userDetails?.is_settings_configured ?? null;

      // ── Build BaseUser for the store ──────────────────────────────────
      const now = new Date().toISOString();
      const displayName = userDetails
        ? ((userDetails.first_name ?? '') + ' ' + (userDetails.last_name ?? '')).trim() || fallbackEmail
        : fallbackEmail;

      // ── Map Cognito roles → BaseUser role using explicit precedence ────
      // Cognito roles (TECHNICAL_ADMIN, CLINICAL_ADMIN, etc.) don't match
      // the BaseUser role enum ('admin'|'user'|'viewer'). Map using a
      // precedence table so the highest-privilege role wins regardless of
      // the incoming array order.
      const ROLE_PRECEDENCE: Record<string, BaseUser['role']> = {
        technical_admin: 'admin',
        clinical_admin: 'admin',
        nicu_manager: 'admin',
        nurse: 'user',
        prep_technician: 'user',
        admin: 'admin',
        user: 'user',
        viewer: 'viewer',
      };
      const PRECEDENCE_ORDER: BaseUser['role'][] = ['admin', 'user', 'viewer'];
      const mappedRoles = parsedRoles
        .map(r => ROLE_PRECEDENCE[r.toLowerCase()])
        .filter((m): m is BaseUser['role'] => !!m);
      const mappedRole: BaseUser['role'] = mappedRoles.length > 0
        ? mappedRoles.reduce((best, r) => PRECEDENCE_ORDER.indexOf(r) < PRECEDENCE_ORDER.indexOf(best) ? r : best)
        : 'user';

      const user: BaseUser = {
        id: staffId || username,
        email: userDetails?.email ?? fallbackEmail,
        name: displayName,
        role: mappedRole,
        createdAt: userDetails?.created_at ?? now,
        updatedAt: now,
      };

      this.user = user;

      // ── Sync mutable config values ────────────────────────────────────
      setAuthContext({
        staffId,
        hospitalId: this.hospitalId || undefined,
        tenantId: this.tenantId || undefined,
      });

      // Schedule proactive token refresh
      this._scheduleProactiveRefresh();

      // ── Post-login side-effects (isSettingsConfigured, locale, entitlements) ─
      void this._runPostLoginSideEffects();

      return true;
    },

    /**
     * Authenticate via the onboarding service login endpoint.
     *
     * POST /onboarding/api/v1/auth/login
     * - Sends `x-tenant-hostname` header (resolved from window.location.hostname)
     * - Parses authenticated response: tokens, user (username, tenant_id, hospital_id, roles)
     * - Calls GET /onboarding/api/v1/users/:username to fetch full user details (staff_id)
     * - Safely parses roles JSON string into string[]
     */
    async login(credentials: LoginCredentials): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.loginErrorCode = null;
      this.challengeData = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const loginStartTime = Date.now();

      try {
        const api = getApiClient();

        // ── Step 1: Call login endpoint ──────────────────────────────────
        // The global interceptor in api.service.ts adds x-tenant-hostname
        // on every request, so no explicit header override is needed here.
        const loginResponse = await api.post<AuthLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/login`,
          { email: credentials.email, password: credentials.password },
        );

        auditResponseStatus = loginResponse.status;

        const loginData = loginResponse.data;

        // ── Handle challenge response ──────────────────────────────────────
        if (loginData.type === 'challenge') {
          this.challengeData = loginData.challenge;
          // Track MFA challenge type so the view can render the right screen
          if (
            loginData.challenge.challengeName === 'SOFTWARE_TOKEN_MFA' ||
            loginData.challenge.challengeName === 'EMAIL_OTP'
          ) {
            this.mfaChallengeType = loginData.challenge.challengeName;
          }
          // Capture setup token issued alongside SELECT_MFA_TYPE for MFA enrollment calls
          this.mfaSetupAccessToken = (loginData as AuthLoginChallenge).access_token ?? null;
          this.pendingEmail = credentials.email;
          // Always persist mfaSetupRequired from the challenge response (required MFA pool)
          if (loginData.mfaSetupRequired) this.mfaSetupRequired = true;

          // First-time MFA setup: /login now returns full tokens with SELECT_MFA_TYPE.
          // Persist them so verify/setup API calls are authenticated and the dashboard
          // is accessible immediately after setup completes.
          if (loginData.challenge.challengeName === 'SELECT_MFA_TYPE') {
            const { access_token, refresh_token, user: selectUser } = loginData as AuthLoginChallenge;
            if (access_token && refresh_token && selectUser) {
              await this._finishLogin({
                accessToken: access_token,
                refreshToken: refresh_token,
                loginUser: selectUser,
                fallbackEmail: credentials.email,
              });
              this.mfaSetupRequired = true;
            }
          }

          // mfaSetupRequired: skip MFA method-selection screen by auto-selecting EMAIL_OTP.
          // Fires for both token-bearing and token-less SELECT_MFA_TYPE responses.
          if (
            loginData.challenge.challengeName === 'SELECT_MFA_TYPE' &&
            loginData.mfaSetupRequired
          ) {
            const username = loginData.challenge.challengeParameters?.USER_ID_FOR_SRP ?? credentials.email;
            const autoRespondResponse = await api.post<AuthLoginResponse>(
              `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
              {
                challengeName: 'SELECT_MFA_TYPE',
                session: loginData.challenge.session,
                username,
                answer: 'EMAIL_OTP',
              },
            );
            const autoRespondData = autoRespondResponse.data;
            if (autoRespondData.type === 'challenge') {
              this.challengeData = autoRespondData.challenge;
              if (autoRespondData.challenge.challengeName === 'EMAIL_OTP') {
                this.mfaChallengeType = 'EMAIL_OTP';
              }
            }
          }

          this.isLoading = false;
          auditStatus = 'SUCCESS';
          return false; // Caller should check challengeData and redirect
        }

        // Validate response type
        if (loginData.type !== 'authenticated') {
          throw new Error(`Unexpected auth response type: ${(loginData as { type: string }).type}`);
        }

        // ── Step 2: Extract tokens and user info from login response ────────
        const {
          access_token: accessToken,
          refresh_token: refreshToken,
          user: loginUser,
        } = loginData.tokens;

        const success = await this._finishLogin({
          accessToken,
          refreshToken,
          loginUser,
          fallbackEmail: credentials.email,
        });

        if (success) {
          auditStatus = 'SUCCESS';
          this.mfaSetupRequired = loginData.mfaSetupRequired === true;
        }

        return success;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'auth.loginFailed');
        auditStatus = 'FAILURE';
        auditErrorCode = 'LOGIN_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Login failed';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          if (apiError && typeof apiError === 'object' && 'code' in apiError) {
            this.loginErrorCode = (apiError as { code: string }).code;
            auditErrorCode = this.loginErrorCode ?? 'LOGIN_FAILED';
          } else {
            auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'LOGIN_FAILED';
          }
          auditResponseStatus = err.response?.status;
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'LOGIN',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/auth/login',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - loginStartTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block login
        }
      }
    },

    /**
     * Complete the NEW_PASSWORD_REQUIRED challenge.
     *
     * POST /onboarding/api/v1/auth/respond-challenge
     * - Sends session, username, challengeName, newPassword
     * - On success, response matches the authenticated login response (tokens + user)
     * - Then follows the same post-login flow (fetch user details, set context, etc.)
     */
    async completeNewPasswordChallenge(newPassword: string): Promise<boolean> {
      if (!this.challengeData) {
        this.error = 'No pending challenge. Please log in again.';
        return false;
      }

      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const { challengeName, session, challengeParameters } = this.challengeData;
        const username = challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
        if (!username) {
          this.challengeData = null;
          this.error = 'Session data is incomplete. Please log in again.';
          return false;
        }

        const resetResponse = await api.post<AuthLoginResponse | AuthResetPasswordResponse>(
          `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
          { challengeName, session, username, newPassword },
        );

        const resetData = resetResponse.data;

        // The respond-challenge endpoint may return either:
        //   1. Wrapped format: { type: 'authenticated', tokens: { ... } }
        //   2. Flat format:    { access_token, refresh_token, user, ... }
        // Normalize to extract tokens consistently.
        let accessToken: string;
        let refreshToken: string;
        let loginUser: { username: string; tenant_id?: string; hospital_id: string; roles: string; default_language?: string };

        if ('type' in resetData && resetData.type === 'authenticated') {
          // Wrapped format
          ({ access_token: accessToken, refresh_token: refreshToken, user: loginUser } = resetData.tokens);
        } else if ('type' in resetData && resetData.type === 'challenge') {
          // MFA challenge returned after password reset — mirror the normal login flow
          const challengeResp = resetData as AuthLoginChallenge;
          this.challengeData = challengeResp.challenge;
          if (
            challengeResp.challenge.challengeName === 'SOFTWARE_TOKEN_MFA' ||
            challengeResp.challenge.challengeName === 'EMAIL_OTP'
          ) {
            this.mfaChallengeType = challengeResp.challenge.challengeName as 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';
          }
          if (challengeResp.challenge.challengeName === 'SELECT_MFA_TYPE') {
            this.mfaSetupAccessToken = challengeResp.access_token ?? null;
            if (challengeResp.access_token && challengeResp.refresh_token && challengeResp.user) {
              await this._finishLogin({
                accessToken: challengeResp.access_token,
                refreshToken: challengeResp.refresh_token,
                loginUser: challengeResp.user,
                fallbackEmail: this.pendingEmail ?? challengeResp.user.username,
              });
            }
            this.mfaSetupRequired = true;
          }
          if (challengeResp.challenge.challengeName === 'MFA_SETUP' || challengeResp.mfaSetupRequired) {
            this.mfaSetupRequired = true;
          }
          auditStatus = 'SUCCESS';
          return false;
        } else if ('access_token' in resetData) {
          // Flat format (token fields at top level)
          ({ access_token: accessToken, refresh_token: refreshToken, user: loginUser } = resetData as AuthResetPasswordResponse);
        } else {
          throw new Error(`Unexpected response format after password reset`);
        }

        // Clear challenge data
        this.challengeData = null;

        // Delegate to shared post-login flow
        const loginResult = await this._finishLogin({
          accessToken,
          refreshToken,
          loginUser,
          fallbackEmail: this.pendingEmail ?? loginUser.username,
        });

        if (loginResult) {
          auditStatus = 'SUCCESS';
          auditResourceId = this.staffId ?? undefined;
          this.mfaSetupRequired = 'mfaSetupRequired' in resetData && (resetData as { mfaSetupRequired?: boolean }).mfaSetupRequired === true;
        } else {
          auditErrorCode = 'SET_PASSWORD_FINISH_LOGIN_FAILED';
          auditErrorMessage = 'Password was set but post-login flow failed';
        }

        return loginResult;
      } catch (err) {
        // SESSION_EXPIRED: backend could not complete the challenge because the
        // Cognito session (3-min TTL) has expired. Clear stale challenge data
        // and redirect to login so the user can start fresh.
        if (isAxiosError(err) && err.response?.status === 401) {
          const code = err.response.data?.error?.code;
          if (code === 'SESSION_EXPIRED') {
            this.challengeData = null;
            this.error = getApiErrorMessage(err, 'auth.sessionExpired');
            auditErrorCode = 'SESSION_EXPIRED';
            auditErrorMessage = this.error ?? undefined;
            return false;
          }
        }
        this.error = getApiErrorMessage(err, 'auth.passwordResetFailed');
        auditErrorCode = 'SET_PASSWORD_FAILED';
        auditErrorMessage = this.error;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'SET_PASSWORD',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Request forgot-password instructions for an email.
     *
     * POST /onboarding/api/v1/auth/forgot-password
     */
    async forgotPassword(payload: ForgotPasswordPayload): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'SUCCESS';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post<{ message: string; email: string }>(
          `${API_ROUTES.ONBOARDING}/auth/forgot-password`,
          { email: payload.email },
        );
        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'auth.resetInstructionsFailed');
        auditStatus = 'FAILURE';
        auditErrorCode = 'FORGOT_PASSWORD_FAILED';
        auditErrorMessage = this.error;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'FORGOT_PASSWORD',
            eventStatus: auditStatus,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * @param options.navigate - When false, skips the `/login` push at the end
     *   (default true). Used by authGuard when it is itself the navigation
     *   that triggered the logout (e.g. a direct /login visit while a session
     *   already exists) — pushing here would start a second, redundant
     *   navigation on top of the guard's own `next()`.
     * @param options.clearFirst - When true, wipes the local session BEFORE the
     *   server call (default false). Used by ActiveSessionView so the previous
     *   user's tokens are gone from the device immediately instead of after the
     *   request settles. The captured tokens are still sent to the server.
     * @param options.timeoutMs - Per-request timeout for the server call, so a
     *   slow or unreachable API can't hold the caller up for the default 30 s.
     */
    async logout(options: { navigate?: boolean; clearFirst?: boolean; timeoutMs?: number } = {}): Promise<void> {
      const { navigate = true, clearFirst = false, timeoutMs } = options;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'SUCCESS';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const logoutStartTime = Date.now();

      // Capture everything the server call and the audit event need — with
      // clearFirst the store is already empty by the time they run.
      const accessToken = this.accessToken;
      const auditUserId     = this.staffId   ?? undefined;
      const auditHospitalId = this.hospitalId ?? undefined;
      const auditEmail      = this.userEmail  ?? undefined;

      const clearLocalSession = async (): Promise<void> => {
        // Clear all browser storage for this app session on logout.
        localStorage.clear();
        sessionStorage.clear();

        // Clear store state (user details, tokens, context)
        this.clearAuth();

        // Locale/entitlements/notifications — shared with handleForcedLogout()
        // so both paths leave a clean slate for the next login.
        await this._resetSessionStores();
      };

      if (clearFirst) await clearLocalSession();

      try {
        const api = getApiClient();
        // PHX-1180: omit access_token entirely when absent (e.g. post-reload) —
        // the schema only accepts a non-empty string or no field at all; the
        // backend falls back to the httpOnly cookie to clear the server session.
        const logoutResponse = await api.post(
          `${API_ROUTES.ONBOARDING}/auth/logout`,
          accessToken ? { access_token: accessToken } : {},
          // Only pass a config object when a timeout was asked for.
          ...(timeoutMs === undefined ? [] : [{ timeout: timeoutMs }]),
        );
        auditResponseStatus = logoutResponse.status;
      } catch (err) {
        // Ignore logout errors, clear local state anyway
        auditStatus = 'FAILURE';
        auditErrorCode = 'LOGOUT_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Logout error';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'LOGOUT_FAILED';
          auditResponseStatus = err.response?.status;
        }
        console.error('Logout error:', err);
      } finally {
        // Emit audit BEFORE clearing auth context (JWT still available).
        // The identity fields were captured above — logAuditEvent is fire-and-forget
        // and the axios token interceptor runs asynchronously (microtask), so by the
        // time it executes the localStorage/Pinia state is already cleared. Passing
        // userId + hospitalId explicitly lets the backend insert even when the
        // JWT is absent (optionalCognitoAuth falls back to body fields).
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'LOGOUT',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/auth/logout',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - logoutStartTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId:     auditUserId,
            hospitalId: auditHospitalId,
            email:      auditEmail,
          });
        } catch {
          // Audit must never block logout
        }

        if (!clearFirst) await clearLocalSession();

        // Redirect to login page (SPA navigation — no full reload)
        if (navigate) router.push('/login');
      }
    },

    setAuth(
      tokens: { accessToken: string; refreshToken: string },
      userData: BaseUser,
      context?: { staffId?: string; hospitalId?: string; tenantId?: string; roles?: string; username?: string },
      details?: UserDetailsResponse | null,
    ) {
      this.accessToken = tokens.accessToken;
      this.refreshToken = tokens.refreshToken;
      this.user = userData;
      this.userDetails = details ?? null;

      // `is_settings_configured` comes from the GET /users/:username response
      // (user.service.ts's getUser()) passed in as `details` — UI routing hint
      // only, the API enforces this independently server-side. No longer
      // decoded from the access token's custom:session_ctx claim, which the
      // Pre-Token-Generation Lambda no longer injects.
      this.isSettingsConfigured = details?.is_settings_configured ?? null;

      // Persist auth context (staffId, hospitalId, tenantId, roles, username)
      if (context) {
        this.staffId = context.staffId ?? null;
        this.hospitalId = context.hospitalId ?? null;
        this.tenantId = context.tenantId ?? null;
        this.roles = context.roles ?? null;
        // Store the cognito:username so token refresh uses the correct value for SECRET_HASH
        if (context.username) {
          this.username = context.username;
        }

        // Parse roles JSON string → string[] (same logic as login())
        if (context.roles) {
          try {
            const parsed = JSON.parse(context.roles);
            if (Array.isArray(parsed)) {
              this.parsedRoles = parsed.filter((r): r is string => typeof r === 'string');
            }
          } catch {
            this.parsedRoles = [];
          }
        } else {
          this.parsedRoles = [];
        }

        // Sync mutable config values
        setAuthContext({
          staffId: context.staffId,
          hospitalId: context.hospitalId,
          tenantId: context.tenantId,
        });
      }

      // Schedule proactive refresh ~60 s before the access token expires
      this._scheduleProactiveRefresh();

      // ── Post-login side-effects (isSettingsConfigured, locale, entitlements) ─
      // Mirrors _finishLogin so the SSO flow stays consistent automatically.
      void this._runPostLoginSideEffects();
    },

    /**
     * Exchange the current refresh token for a new token pair.
     *
     * Called by:
     *   • The 401 response interceptor in api.service.ts (reactive)
     *   • The proactive timer scheduled in setAuth() (~60 s before expiry)
     *
     * Endpoint: POST /onboarding/api/v1/auth/refresh
     * Request:  { refresh_token, username }
     * Response: { access_token, refresh_token, ... }
     *
     * Uses a shared in-flight mutex (_refreshPromise) so concurrent callers
     * (proactive timer + 401 interceptor) dedupe to a single refresh operation
     * and avoid token-rotation races.
     *
     * On success → stores new tokens and reschedules the proactive timer.
     * On failure → clears auth state and redirects to /login.
     */
    async refreshAccessToken(): Promise<boolean> {
      // Dedupe: if a refresh is already in flight, return its result
      if (_refreshPromise) return _refreshPromise;

      _refreshPromise = this._doRefresh();
      try {
        return await (_refreshPromise as Promise<boolean>);
      } finally {
        _refreshPromise = null;
      }
    },

    /** Internal refresh implementation (called via mutex wrapper). */
    async _doRefresh(): Promise<boolean> {
      // Prefer this.username (the cognito:username used at login) for the SECRET_HASH
      // computation on the backend. Cognito requires the same username value that was
      // used during login. Fall back to staffId (sub UUID) only if username is absent
      // (e.g., migrated sessions from before this fix).
      const cognitoUsername = this.username ?? this.staffId;
      if (!cognitoUsername) {
        this.clearAuth();
        router.push('/login');
        return false;
      }

      try {
        const api = getApiClient();
        // PHX-1180: refreshToken is no longer persisted, so it's null after a
        // page reload — the backend falls back to the httpOnly tms_refresh_token cookie
        // in that case. Still send it when present (e.g. immediately post-login,
        // same page load) for the non-cookie/legacy path.
        const body: { username: string; refresh_token?: string } = { username: cognitoUsername };
        if (this.refreshToken) {
          body.refresh_token = this.refreshToken;
        }

        const { data } = await api.post<{ access_token: string; refresh_token?: string }>(
          `${API_ROUTES.ONBOARDING}/auth/refresh`,
          body,
        );

        this.accessToken = data.access_token;
        if (data.refresh_token) {
          this.refreshToken = data.refresh_token;
        }

        // Reschedule proactive refresh for the new access token
        this._scheduleProactiveRefresh();

        return true;
      } catch {
        this.clearAuth();
        router.push('/login');
        return false;
      }
    },

    // ── MFA actions ────────────────────────────────────────────────────────

    /**
     * Answer a SOFTWARE_TOKEN_MFA or EMAIL_OTP challenge after login.
     * POST /onboarding/api/v1/auth/respond-challenge
     */
    async respondMfaChallenge(code: string): Promise<boolean> {
      if (!this.challengeData) {
        this.error = 'No active MFA challenge. Please log in again.';
        return false;
      }

      const { challengeName, session, challengeParameters } = this.challengeData;
      const username = challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      if (!username) {
        this.error = 'Session data is incomplete. Please log in again.';
        return false;
      }

      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const startTime = Date.now();

      try {
        const api = getApiClient();
        const response = await api.post<AuthResetPasswordResponse>(
          `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
          { challengeName, session, username, code },
        );

        this.challengeData = null;
        this.mfaChallengeType = null;

        const { access_token: accessToken, refresh_token: refreshToken, user: loginUser } = response.data;
        const ok = await this._finishLogin({ accessToken, refreshToken, loginUser, fallbackEmail: this.pendingEmail ?? loginUser.username });
        if (ok) {
          auditStatus = 'SUCCESS';
          // Persist mfaSetupRequired so the auth guard redirects to /mfa-setup when needed
          this.mfaSetupRequired = response.data.mfaSetupRequired === true;
        }
        return ok;
      } catch (err) {
        if (isAxiosError(err)) {
          const code = err.response?.data?.error?.code;
          auditErrorCode = code ?? 'MFA_CHALLENGE_FAILED';
          auditResponseStatus = err.response?.status;
          if (code === 'SESSION_EXPIRED' || code === 'MFA_SESSION_EXPIRED') {
            this.challengeData = null;
            this.mfaChallengeType = null;
            this.error = getApiErrorMessage(err, 'auth.sessionExpired');
          } else {
            this.error = getApiErrorMessage(err, 'auth.verificationFailed');
          }
          auditErrorMessage = this.error ?? undefined;
        } else {
          this.error = getApiErrorMessage(err, 'auth.verificationFailed');
          auditErrorCode = 'MFA_CHALLENGE_FAILED';
          auditErrorMessage = this.error ?? undefined;
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_CHALLENGE_VERIFY',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/auth/respond-challenge',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - startTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Fetch available MFA methods for the current user.
     * GET /onboarding/api/v1/mfa/methods
     * Maps API values (TOTP, EMAIL_OTP) to UI method IDs (authenticator, email).
     */
    async fetchMfaMethods(): Promise<('authenticator' | 'email')[]> {
      try {
        const api = getApiClient();
        const response = await api.get<{ methods: string[] }>(`${API_ROUTES.ONBOARDING}/mfa/methods`);
        const methods: string[] = response.data?.methods ?? [];
        const mapped: ('authenticator' | 'email')[] = [];
        if (methods.includes('TOTP')) mapped.push('authenticator');
        if (methods.includes('EMAIL_OTP')) mapped.push('email');
        return mapped.length > 0 ? mapped : ['authenticator', 'email'];
      } catch {
        return ['authenticator', 'email'];
      }
    },

    /**
     * Begin TOTP setup — returns QR code data URL and otpauth URI.
     * POST /onboarding/api/v1/mfa/setup/totp/initiate (no body)
     */
    async initiateTotpSetup(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.totpQrData = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const startTime = Date.now();

      try {
        const api = getApiClient();
        const response = await api.post<TotpInitiateResponse>(
          `${API_ROUTES.ONBOARDING}/mfa/setup/totp/initiate`,
        );
        this.totpQrData = response.data;
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_TOTP_INITIATE_FAILED';
          auditResponseStatus = err.response?.status;
          this.error = getApiErrorMessage(err, 'auth.mfaSetupTotpInitiateFailed');
        } else {
          this.error = getApiErrorMessage(err, 'auth.mfaSetupTotpInitiateFailed');
          auditErrorCode = 'MFA_SETUP_TOTP_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_INITIATE',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/mfa/setup/totp/initiate',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - startTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Verify the first TOTP code to activate MFA.
     * POST /onboarding/api/v1/mfa/setup/totp/verify
     */
    async verifyTotpSetup(code: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const startTime = Date.now();

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/totp/verify`, { code });
        this.totpQrData = null;
        this.mfaSetupRequired = false;
        this.challengeData = null;
        this.mfaSetupAccessToken = null;
        // Re-apply auth context so config.hospitalId/staffId are populated for
        // immediate navigation, then re-run post-login side effects (entitlements,
        // locale) mirroring what _finishLogin does via respondMfaChallenge.
        setAuthContext({
          staffId: this.staffId ?? undefined,
          hospitalId: this.hospitalId ?? undefined,
          tenantId: this.tenantId ?? undefined,
        });
        void this.resetEntitlementsForReload();
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          const errCode = err.response?.data?.error?.code;
          // 422 TOTP_SETUP_FAILED — QR is invalid; caller must re-initiate
          if (errCode === 'TOTP_SETUP_FAILED') {
            this.totpQrData = null;
          }
          this.error = getApiErrorMessage(err, 'auth.totpVerificationFailed');
          auditErrorCode = errCode ?? 'MFA_SETUP_TOTP_VERIFY_FAILED';
          auditResponseStatus = err.response?.status;
        } else {
          this.error = getApiErrorMessage(err, 'auth.totpVerificationFailed');
          auditErrorCode = 'MFA_SETUP_TOTP_VERIFY_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_VERIFY',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/mfa/setup/totp/verify',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - startTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Send a verification code to the user's registered email address.
     * POST /onboarding/api/v1/mfa/setup/email/initiate (no body)
     * Call verifyEmailOtp() next to confirm the code and activate Email OTP.
     */
    async setupEmailOtp(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const startTime = Date.now();

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/email/initiate`);
        this.mfaSetupRequired = false;
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_EMAIL_INITIATE_FAILED';
          auditResponseStatus = err.response?.status;
          this.error = getApiErrorMessage(err, 'auth.mfaSetupEmailInitiateFailed');
        } else {
          this.error = getApiErrorMessage(err, 'auth.mfaSetupEmailInitiateFailed');
          auditErrorCode = 'MFA_SETUP_EMAIL_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_INITIATE',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/mfa/setup/email/initiate',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - startTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Confirm the email OTP code to activate Email OTP MFA.
     * POST /onboarding/api/v1/mfa/setup/email/verify
     */
    async verifyEmailOtp(code: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResponseStatus: number | undefined;
      const startTime = Date.now();

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/email/verify`, { code });
        this.mfaSetupRequired = false;
        this.mfaSetupAccessToken = null;
        this.challengeData = null;
        // Re-apply auth context so config.hospitalId/staffId are populated for
        // immediate navigation, then re-run post-login side effects (entitlements,
        // locale) mirroring what _finishLogin does via respondMfaChallenge.
        setAuthContext({
          staffId: this.staffId ?? undefined,
          hospitalId: this.hospitalId ?? undefined,
          tenantId: this.tenantId ?? undefined,
        });
        void this.resetEntitlementsForReload();
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_EMAIL_VERIFY_FAILED';
          auditResponseStatus = err.response?.status;
          this.error = getApiErrorMessage(err, 'auth.emailVerificationFailed');
        } else {
          this.error = getApiErrorMessage(err, 'auth.emailVerificationFailed');
          auditErrorCode = 'MFA_SETUP_EMAIL_VERIFY_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_VERIFY',
            eventStatus: auditStatus,
            httpMethod: 'POST',
            apiPath: '/onboarding/api/v1/mfa/setup/email/verify',
            apiVersion: 'v1',
            responseStatus: auditResponseStatus,
            durationMs: Date.now() - startTime,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Read the current MFA configuration for the logged-in user.
     * GET /onboarding/api/v1/mfa/status
     */
    async getMfaStatus(): Promise<MfaStatus | null> {
      try {
        const api = getApiClient();
        const response = await api.get<MfaStatus>(`${API_ROUTES.ONBOARDING}/mfa/status`);
        return response.data;
      } catch {
        return null;
      }
    },

    // ── In-auth MFA setup (Required MFA pools — MFA_SETUP challenge, no token yet) ──

    /**
     * Step 1 of in-session TOTP setup: exchange the MFA_SETUP session for a QR code.
     * POST /onboarding/api/v1/auth/mfa-setup/totp/initiate
     * Stores the new session (returned in response) back into challengeData.
     */
    async initiateInAuthTotpSetup(): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      this.totpQrData = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const response = await api.post<{ session: string; secretCode: string; otpauthUri: string; qrCodeDataUrl: string }>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/totp/initiate`,
          { session, username, loginType: 'default' },
        );
        // Replace session with the fresh one returned by the endpoint
        this.challengeData = { ...this.challengeData, session: response.data.session };
        this.totpQrData = { qrCodeDataUrl: response.data.qrCodeDataUrl, otpauthUri: response.data.otpauthUri };
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'IN_AUTH_TOTP_INITIATE_FAILED';
          this.error = getApiErrorMessage(err, 'auth.mfaSetupTotpInitiateFailed');
        } else {
          this.error = getApiErrorMessage(err, 'auth.mfaSetupTotpInitiateFailed');
          auditErrorCode = 'IN_AUTH_TOTP_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_INITIATE',
            eventStatus: auditStatus,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch { /* audit must never block the flow */ }
      }
    },

    /**
     * Step 2 of in-session TOTP setup: verify the code and complete the MFA_SETUP challenge.
     * POST /onboarding/api/v1/auth/mfa-setup/totp/complete
     * On success, issues tokens and calls _finishLogin.
     */
    async completeInAuthTotpSetup(code: string): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const response = await api.post<AuthLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/totp/complete`,
          { session, username, code, loginType: 'default' },
        );
        const data = response.data;
        if ('type' in data && data.type === 'authenticated') {
          this.challengeData = null;
          this.totpQrData = null;
          const { access_token: accessToken, refresh_token: refreshToken, user: loginUser } = data.tokens;
          const ok = await this._finishLogin({ accessToken, refreshToken, loginUser, fallbackEmail: this.pendingEmail ?? loginUser.username });
          if (ok) {
            auditStatus = 'SUCCESS';
            this.mfaSetupRequired = false;
            this.mfaSetupAccessToken = null;
          }
          return ok;
        }
        // Cascade challenge (e.g. EMAIL_OTP) — treat like a normal challenge
        if ('type' in data && data.type === 'challenge') {
          this.challengeData = data.challenge;
          if (data.challenge.challengeName === 'EMAIL_OTP') this.mfaChallengeType = 'EMAIL_OTP';
          auditStatus = 'SUCCESS';
          return false;
        }
        throw new Error('Unexpected response from mfa-setup/totp/complete');
      } catch (err) {
        if (isAxiosError(err)) {
          const errCode = err.response?.data?.error?.code;
          if (errCode === 'TOTP_SETUP_FAILED') this.totpQrData = null;
          this.error = getApiErrorMessage(err, 'auth.totpVerificationFailed');
          auditErrorCode = errCode ?? 'IN_AUTH_TOTP_COMPLETE_FAILED';
        } else {
          this.error = getApiErrorMessage(err, 'auth.totpVerificationFailed');
          auditErrorCode = 'IN_AUTH_TOTP_COMPLETE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_VERIFY',
            eventStatus: auditStatus,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch { /* audit must never block the flow */ }
      }
    },

    /**
     * In-session Email OTP setup: enable Email OTP and complete the MFA_SETUP challenge.
     * POST /onboarding/api/v1/auth/mfa-setup/email/complete
     * Cognito typically returns an EMAIL_OTP challenge immediately; handle it via respondMfaChallenge.
     */
    async completeInAuthEmailSetup(): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const response = await api.post<AuthLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/email/complete`,
          { session, username, loginType: 'default' },
        );
        const data = response.data;
        if ('type' in data && data.type === 'challenge' && data.challenge.challengeName === 'EMAIL_OTP') {
          // Cognito sends the OTP immediately; update challengeData so respondMfaChallenge works
          this.challengeData = data.challenge;
          this.mfaChallengeType = 'EMAIL_OTP';
          auditStatus = 'SUCCESS';
          return true; // caller transitions to email-verify step
        }
        if ('type' in data && data.type === 'authenticated') {
          this.challengeData = null;
          const { access_token: accessToken, refresh_token: refreshToken, user: loginUser } = data.tokens;
          const ok = await this._finishLogin({ accessToken, refreshToken, loginUser, fallbackEmail: this.pendingEmail ?? loginUser.username });
          if (ok) { auditStatus = 'SUCCESS'; this.mfaSetupRequired = false; }
          return ok;
        }
        throw new Error('Unexpected response from mfa-setup/email/complete');
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'IN_AUTH_EMAIL_COMPLETE_FAILED';
          this.error = getApiErrorMessage(err, 'auth.emailOtpEnableFailed');
        } else {
          this.error = getApiErrorMessage(err, 'auth.emailOtpEnableFailed');
          auditErrorCode = 'IN_AUTH_EMAIL_COMPLETE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_INITIATE',
            eventStatus: auditStatus,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
            hospitalId: this.hospitalId ?? undefined,
          });
        } catch { /* audit must never block the flow */ }
      }
    },

    clearAuth() {
      this._clearRefreshTimer();
      this.accessToken = null;
      this.refreshToken = null;
      this.user = null;
      this.userDetails = null;
      this.username = null;
      this.staffId = null;
      this.hospitalId = null;
      this.tenantId = null;
      this.roles = null;
      this.parsedRoles = [];
      this.defaultLanguage = null;
      this.isSettingsConfigured = null;
      this.challengeData = null;
      this.mfaSetupRequired = false;
      this.mfaChallengeType = null;
      this.totpQrData = null;
      this.mfaSetupAccessToken = null;
      this.pendingEmail = null;
      this.error = null;

      // Remove persisted settings flag so the next login gets a fresh check
      clearIsSettingsConfigured();

      // Reset the persisted idle-activity timestamp so the next login isn't
      // immediately flagged as idle-expired by a stale value.
      clearLastActivityTimestamp();

      // Drop cached onboarding progress too — on a shared workstation, leaving it
      // behind would let one admin's partial progress be read as the next
      // admin's, and it is re-fetched from DynamoDB on the next login anyway.
      clearOnboardingProgress();

      // Clear auth-derived config values so stale IDs don't leak
      clearAuthContext();
    },

    /**
     * Resets locale, entitlements, and notification store state. Shared by
     * logout() and handleForcedLogout() — both leave the SPA running (Pinia
     * stores survive the navigation), so without this the next login would
     * inherit the previous user's notificationStore.isInitialLoadComplete /
     * critical queue and stale entitlements.
     */
    async _resetSessionStores(): Promise<void> {
      // Clear tenant context and mark the full locale bundle as unloaded so
      // the next login re-fetches for the (potentially different) tenant.
      try {
        const localeStore = useLocaleStore();
        localeStore.resetOnLogout();
      } catch {
        // Non-fatal
      }

      try {
        const { useEntitlementsStore } = await import('@/stores/entitlements.store');
        const entitlementsStore = useEntitlementsStore();
        entitlementsStore.reset();
      } catch {
        // Non-fatal
      }

      // sessionStorage.clear() (called by both callers) already wipes the
      // persisted recall-banner-dismissed key, but the in-memory
      // sessionDismissedRecallIds array lives in the notification store and
      // must be cleared too so dismissed recalls reappear on the next login
      // within the same tab (without a page reload).
      try {
        const { useNotificationStore } = await import('@/stores/notification.store');
        const notificationStore = useNotificationStore();
        notificationStore.$reset();
      } catch {
        // Non-fatal
      }
    },

    /**
     * Clears auth state and redirects to /login without calling the logout API.
     * Used when the session is already known to be invalid server-side (e.g. a
     * refresh-token retry that still 401s) — hitting /auth/logout would just 401 again.
     */
    forceLogout() {
      this.clearAuth();
      router.push('/login');
    },

    /**
     * Handles a `FORCE_LOGOUT` SSE event (e.g. an admin just disabled this
     * account). Wipes tokens/storage and redirects to /login with a reason
     * so every open tab for this user logs out immediately, instead of
     * waiting for the next API call to 401.
     *
     * Reuses the same locale/entitlements/notifications reset as logout() —
     * without it, notificationStore.isInitialLoadComplete stays true across
     * the SPA navigation and the next login would skip hydration, exposing
     * the previous user's notifications/critical queue.
     */
    async handleForcedLogout(reason: ForceLogoutReason): Promise<void> {
      localStorage.clear();
      sessionStorage.clear();
      this.clearAuth();
      await this._resetSessionStores();
      router.push({ path: '/login', query: { reason: reason.toLowerCase() } });
    },

    updateUser(userData: Partial<BaseUser>) {
      if (this.user) {
        this.user = { ...this.user, ...userData };
      }
    },

    hasRole(role: string): boolean {
      const target = role.trim().toUpperCase();
      if (!target) return false;
      return getEffectiveRoles(this.parsedRoles).some((r) => r === target);
    },

    hasAnyRole(roles: string[]): boolean {
      return roles.some((role) => this.hasRole(role));
    },

    hasOnlyRoles(roles: string[]): boolean {
      const allowed = new Set(
        roles
          .map((role) => role.trim().toUpperCase())
          .filter((role) => role.length > 0),
      );
      if (allowed.size === 0) return false;

      const effective = getEffectiveRoles(this.parsedRoles);
      if (effective.length === 0) return false;

      return effective.every((role) => allowed.has(role));
    },

    // ── Internal helpers ───────────────────────────────────────────────────

    /**
     * Schedule a proactive token refresh ~60 s before the access token expires.
     * Avoids 401 round-trips for long-lived sessions.
     */
    _scheduleProactiveRefresh() {
      this._clearRefreshTimer();

      if (!this.accessToken) return;

      const exp = decodeJwtExp(this.accessToken);
      if (!exp) return;

      const nowSec = Math.floor(Date.now() / 1000);
      // Refresh 60 s before expiry, minimum 10 s from now
      const delaySec = Math.max(exp - nowSec - 60, 10);

      _refreshTimerId = setTimeout(() => {
        this.refreshAccessToken();
      }, delaySec * 1000);
    },

    /** Cancel any pending proactive refresh timer. */
    _clearRefreshTimer() {
      if (_refreshTimerId) {
        clearTimeout(_refreshTimerId);
        _refreshTimerId = null;
      }
    },

    /**
     * Re-schedule the proactive refresh timer after a page reload.
     *
     * The custom persistence plugin restores state via `$patch` but does not
     * call `setAuth()`, so the timer is never started.  Call this once from
     * `main.ts` after `setupPinia()` to close the gap.
     *
     * Also re-hydrates mutable config values (`staffId`, `hospitalId`,
     * `tenantId`) from persisted auth state so that `config.*` reflects
     * the authenticated user's context after a page refresh.
     */
    initRefreshTimer() {
      // PHX-1180: one-time migration — purge legacy standalone OIDC token keys
      // that older app versions wrote directly to localStorage (bypassing the
      // pinia-persist allow-list), so they don't linger for existing sessions.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('idToken');

      if (this.accessToken) {
        this._scheduleProactiveRefresh();
      } else if (this.username || this.staffId) {
        // A laptop locked/asleep (or the tab fully closed) across a gap longer than the idle
        // timeout means the in-memory idle timer in App.vue never got a chance to fire — force
        // a fresh login instead of silently resuming via the refresh cookie.
        if (hasIdleTimeoutElapsed()) {
          // Go through the real logout() (not just clearAuth()) so the httpOnly refresh
          // cookie is actually invalidated server-side via the cookie-fallback /auth/logout
          // call — otherwise the "logged out" browser would still hold a valid refresh
          // credential despite appearing logged out locally.
          void this.logout();
        } else {
          // PHX-1180: accessToken/refreshToken are no longer persisted, so a page
          // reload leaves them null in memory even though the httpOnly tms_refresh_token
          // cookie may still be valid. Re-establish an in-memory access token via
          // the same refresh endpoint (cookie fallback) so proactive-refresh
          // scheduling and any legacy Authorization-header consumers keep working.
          // Fails silently into the normal 401/redirect-to-login flow if the
          // cookie is also gone/expired.
          void this.refreshAccessToken();
        }
      }

      // Hydrate mutable config from persisted auth context
      if (this.staffId || this.hospitalId || this.tenantId) {
        setAuthContext({
          staffId: this.staffId ?? undefined,
          hospitalId: this.hospitalId ?? undefined,
          tenantId: this.tenantId ?? undefined,
        });
      }
    },
  },

  persist: {
    paths: [
      'user',
      'userDetails',
      // PHX-1180: accessToken/refreshToken are intentionally NOT persisted —
      // they used to sit in localStorage indefinitely, readable by any XSS.
      // The real session now lives in an httpOnly cookie the backend sets;
      // these fields are in-memory-only for the lifetime of the tab.
      'username',
      'staffId',
      'hospitalId',
      'tenantId',
      'roles',
      'parsedRoles',
      'defaultLanguage',
      'mfaSetupRequired',
    ],
  },
});
